import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ViewerShell } from "./ViewerShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";

// Audit V1: public demo-pack landing page. Mints an issuance on the fly when
// the visitor identifies themselves, then drops them into the standard
// /view/:token flow.
type PackPreview = {
  slug: string;
  welcomeMessage: string | null;
  playlistName: string;
  senderName: string;
};

export function DemoPackLandingPage() {
  const { slug } = useParams<{ slug: string }>();
  const nav = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["demo-pack-preview", slug],
    queryFn: () => api.get<PackPreview>(`/api/pack/${slug}`),
    enabled: !!slug,
    retry: false,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");

  const claim = useMutation({
    mutationFn: () =>
      api.post<{ token: string }>(`/api/pack/${slug}/claim`, {
        name,
        email,
        company: company || undefined,
      }),
    onSuccess: (resp) => {
      nav(`/view/${resp.token}`);
    },
    onError: (err) =>
      toast.error(err instanceof Error ? err.message : "Could not open pack"),
  });

  if (isLoading) {
    return (
      <ViewerShell>
        <div className="text-center py-20 text-navy-200">Loading…</div>
      </ViewerShell>
    );
  }
  if (error || !data) {
    return (
      <ViewerShell>
        <div className="text-center py-16 space-y-2">
          <div className="font-head text-2xl">This pack is no longer available.</div>
          <p className="text-navy-200 text-sm">
            It may have been revoked, expired, or the link is wrong.
          </p>
        </div>
      </ViewerShell>
    );
  }

  return (
    <ViewerShell>
      <div className="space-y-6">
        <section className="text-center space-y-2">
          <h1 className="font-head text-3xl md:text-4xl font-semibold leading-tight">
            {data.playlistName}
          </h1>
          <p className="text-navy-200 font-medium">
            Shared by {data.senderName}
          </p>
        </section>
        {data.welcomeMessage && (
          <section className="bg-viewer-soft rounded-md p-5 text-center">
            <p className="text-white whitespace-pre-line">{data.welcomeMessage}</p>
          </section>
        )}
        <section className="space-y-3">
          <div className="font-head text-xl">Tell us who you are to start</div>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company">Company (optional)</Label>
              <Input
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
              />
            </div>
          </div>
          <Button
            size="lg"
            className="w-full"
            disabled={!name || !email || claim.isPending}
            onClick={() => claim.mutate()}
          >
            {claim.isPending ? "Opening…" : "Open the demo"}
          </Button>
        </section>
      </div>
    </ViewerShell>
  );
}
